import React, { useCallback, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { Check, ChevronDown, FileDownIcon, X } from 'lucide-react'

import { Select } from '@/components'
import InputSearch from '@/components/InputSearch'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { HistoryFilters, HistoryTimeFrame } from '@/features/audit/types'
import { useUserManagementList } from '@/features/user-management/hooks'
import { UserStatus } from '@/schema'
import { cn, getEndOfDayUTC, getStartOfDayUTC } from '@/utils/common'

import { TABLE_PAGE_SIZE } from '../../../utils/constants'

const LOG_TYPE_PILLS = [
  { label: 'Companies', value: 'COMPANY' },
  { label: 'Projects', value: 'PROJECT' },
  { label: 'Deliverable Sets', value: 'DELIVERABLE_SET' },
  { label: 'TFL Files', value: 'TFL_FILE' },
] as const

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'THIS_WEEK', label: 'This week' },
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'THIS_QUARTER', label: 'This quarter' },
  { value: 'CUSTOM', label: 'Custom' },
]

export interface HistoryFiltersBarProps {
  filters: HistoryFilters
  onFiltersChange: (next: Partial<HistoryFilters>) => void
  onExportClick?: (timestamp: string) => void
  isExporting?: boolean
}

const HistoryFiltersBar: React.FC<HistoryFiltersBarProps> = ({
  filters,
  onFiltersChange,
  onExportClick,
  isExporting,
}) => {
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false)
  const [userSearchText, setUserSearchText] = useState('')

  const { data: { data: usersData = [] } = {} } = useUserManagementList({
    filters: { status: [UserStatus.ACTIVE] },
    rowsPerPage: TABLE_PAGE_SIZE * 10,
  })

  const userOptions = useMemo(
    () =>
      usersData.map((u) => ({
        value: u.id,
        label:
          [u.first_name, u.last_name].filter(Boolean).join(' ') ||
          u.email ||
          u.id,
      })).filter((option) =>
        option.label.toLowerCase().includes(userSearchText.toLowerCase())
      ),
    [usersData, userSearchText]
  )

  const userButtonLabel = useMemo(() => {
    if (!filters.user_id || filters.user_id.length === 0) {
      return 'All users'
    }
    const firstOption = userOptions.find(
      (option) => option.value === filters?.user_id?.[0]
    )
    const firstLabel = firstOption ? String(firstOption.label) : ''

    if (filters.user_id.length === 1) {
      return firstLabel
    }
    return `${firstLabel}, +${filters.user_id.length - 1}`
  }, [filters.user_id, userOptions])

  const handlePillToggle = useCallback(
    (value: string) => {
      const current = filters.logTypes ?? []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      onFiltersChange({ logTypes: next })
    },
    [filters.logTypes, onFiltersChange]
  )

  const handleDateRangeChange = useCallback(
    (value: string) => {
      onFiltersChange({
        timeFrame: value === 'all' ? null : (value as HistoryTimeFrame) || null,
        startDate: null,
        endDate: null,
      })
    },
    [onFiltersChange]
  )

  const handleStartDateChange = useCallback(
    (date: Date | undefined) => {
      const iso = date ? getStartOfDayUTC(date) : null
      onFiltersChange({ startDate: iso })
    },
    [onFiltersChange]
  )

  const handleEndDateChange = useCallback(
    (date: Date | undefined) => {
      const iso = date ? getEndOfDayUTC(date) : null
      onFiltersChange({ endDate: iso })
    },
    [onFiltersChange]
  )

  return (
    <div className="flex items-end justify-between gap-3 w-full">
      {/* ── LEFT: log_type pill buttons ─────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {LOG_TYPE_PILLS.map((pill) => {
          const active = (filters.logTypes ?? []).includes(pill.value)
          return (
            <button
              key={pill.value}
              type="button"
              onClick={() => handlePillToggle(pill.value)}
              className={cn(
                'h-8 px-3 text-sm rounded-sm border border-solid transition-colors whitespace-nowrap',
                active
                  ? 'bg-blue-50 border-blue-600 text-blue-600'
                  : 'bg-white border-gray-light-300 text-primary-900 hover:border-primary-900'
              )}
            >
              {pill.label}
            </button>
          )
        })}
      </div>

      {/* ── RIGHT: User, Time frame, Export ─────────────────────────────────── */}
      <div className="flex items-end gap-2 flex-shrink-0">
        {/* User filter */}
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-secondary-700 block cursor-default">
            Users
          </span>
          <div>
            <Popover
              open={isUserPopoverOpen}
              onOpenChange={setIsUserPopoverOpen}
            >
              <PopoverTrigger asChild>
                <button
                  id="user-filter"
                  type="button"
                  className="relative inline-flex items-center justify-between h-10 px-3.5 py-2.5 text-sm font-medium text-primary-900 border border-solid border-[--Colors-Gray-light-300] bg-white rounded-lg shadow-xs hover:bg-gray-50 focus:outline-none focus:border-blue-500 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 cursor-pointer min-w-[140px]"
                >
                  <span className="truncate">{userButtonLabel}</span>
                  {filters.user_id && filters.user_id.length > 0 ? (
                    <X
                      size={16}
                      className="ml-2 text-gray-500 hover:text-gray-700"
                      onClick={(e) => {
                        e.stopPropagation()
                        onFiltersChange({ user_id: undefined })
                        setIsUserPopoverOpen(false)
                      }}
                    />
                  ) : (
                    <ChevronDown size={16} className="ml-2 text-gray-500" />
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="sm:min-w-[200px] p-0"
                side="bottom"
                align="end"
              >
                <div className="p-2">
                  <InputSearch
                    searchText={userSearchText}
                    setSearchText={setUserSearchText}
                    placeholder="Search users"
                    className="w-full h-8 px-2 text-sm"
                  />
                </div>
                <div className="py-1 px-1.5 space-y-0.5 max-h-[300px] overflow-y-auto w-full">
                  {userOptions.map((option) => {
                    const isSelected = filters.user_id?.includes(option.value)
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={cn(
                          'p-sm font-medium text-sm cursor-pointer rounded-sm hover:bg-primary-hover flex gap-2 items-center justify-between w-full text-left bg-transparent border-none',
                          isSelected ? 'bg-primary-hover' : ''
                        )}
                        onClick={() => {
                          const currentUsers = filters.user_id || []
                          if (isSelected) {
                            const newUsers = currentUsers.filter(
                              (s) => s !== option.value
                            )
                            onFiltersChange({
                              user_id:
                                newUsers.length > 0 ? newUsers : undefined,
                            })
                          } else {
                            onFiltersChange({
                              user_id: [...currentUsers, option.value],
                            })
                          }
                        }}
                      >
                        <span className="truncate">{option.label}</span>
                        {isSelected && (
                          <Check
                            size={16}
                            className="text-foreground-fg-brand-primary-600 flex-shrink-0"
                          />
                        )}
                      </button>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Date range */}
        <div className="space-y-1.5">
          <label
            htmlFor="timeframe"
            className="text-sm font-medium text-secondary-700 block"
          >
            Date Range
          </label>
          <Select
            value={filters.timeFrame || 'all'}
            onChange={handleDateRangeChange}
            options={DATE_RANGE_OPTIONS}
            placeholder="All time"
            className="min-w-[130px]"
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
            disableSorting
          />
        </div>

        {/* Custom date pickers */}
        {filters.timeFrame === 'CUSTOM' && (
          <>
            <DatePicker
              className="w-40"
              placeholder="MM/DD/YYYY"
              toDate={
                filters.endDate ? dayjs(filters.endDate).toDate() : undefined
              }
              fromDate={
                filters.endDate
                  ? dayjs(filters.endDate).subtract(90, 'day').toDate()
                  : undefined
              }
              onDateChange={handleStartDateChange}
              value={
                filters.startDate
                  ? dayjs(filters.startDate).toDate()
                  : undefined
              }
            />
            <DatePicker
              className="w-40"
              placeholder="MM/DD/YYYY"
              fromDate={
                filters.startDate
                  ? dayjs(filters.startDate).toDate()
                  : undefined
              }
              toDate={
                filters.startDate
                  ? dayjs(filters.startDate).add(90, 'day').toDate()
                  : undefined
              }
              onDateChange={handleEndDateChange}
              value={
                filters.endDate ? dayjs(filters.endDate).toDate() : undefined
              }
            />
          </>
        )}

        {/* Export button */}
        <Button
          variant="solid"
          color="primary"
          onClick={() => onExportClick?.(dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'))}
          disabled={
            isExporting ||
            !filters.timeFrame ||
            (filters.timeFrame === 'CUSTOM' &&
              (!filters.startDate || !filters.endDate))
          }
          className="flex items-center gap-1 h-10"
          leadingIcon={<FileDownIcon size={16} />}
        >
          Export
        </Button>
      </div>
    </div>
  )
}

export default HistoryFiltersBar
